import {
  FetchAllCountriesQuery,
  FetchAllCountriesQueryVariables,
  TagsV2Query,
  TagsV2QueryVariables,
  useContactSourceTypesQuery,
} from '@/types/generated/graphql';
import { useLazyCursorPagination } from '@shared/hooks/use-lazy-cursor-pagination';
import { CURSOR_ALL_COUNTRIES, CURSOR_TAG_V2 } from '@/graphql/cursor.graphql';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

export const contact_filter_key = {
  country_code: 'countryCode',
  source_types: 'sourceTypes',
  tags: 'tags',
};

export function useContactAdvancedFilter() {
  // search queries
  const [searchParams, setSearchParams] = useSearchParams();
  const allFiltersIterator = searchParams.keys();
  const advancedFiltersCount = Array.from(allFiltersIterator).filter((v) => v !== 'q').length;
  // states
  const [isFilterOpen, setFilterOpen] = useState(false);
  //apis
  const { data: sourceTypes, loading: sourceLoading } = useContactSourceTypesQuery({
    skip: !isFilterOpen && !advancedFiltersCount,
  });
  const countriesFilterLogic = useCountriesFilter(isFilterOpen);
  const tagsFilterLogic = useTagsFilter(isFilterOpen);
  // transformation
  const sources = sourceTypes?.contactSourceTypes?.data?.map((source) => ({
    label: source?.sourceType,
    value: String([source?.sourceType, source?.sourceType]),
  }));

  function handleSource(value: string[]) {
    searchParams.delete(contact_filter_key.source_types);
    value.map((v) => {
      searchParams.append(contact_filter_key.source_types, v);
    });

    setSearchParams(searchParams);
  }

  return {
    countriesFilterLogic,
    tagsFilterLogic,
    appliedFilterCount: advancedFiltersCount,
    sources,
    sourceLoading,
    isFilterOpen,
    setFilterOpen,
    handleSource,
  };
}

function useCountriesFilter(isFilterOpen: boolean) {
  // search queries
  const [searchParams, setSearchParams] = useSearchParams();
  const checkCountryApplied = (countryCode: string) => searchParams.getAll('countryCode').includes(countryCode);
  // states
  const [countrySearch, setCountrySearch] = useState('');
  //apis
  const { fetchQuery, data, hasNextPage, loadNextRef } = useLazyCursorPagination<
    FetchAllCountriesQuery,
    FetchAllCountriesQueryVariables
  >({
    schema: CURSOR_ALL_COUNTRIES,
    responseDataKey: 'fetchAllCountries',
  });

  const countries = data?.data?.map((country) => ({
    iconUrl: country?.flag,
    label: country?.name + '',
    code: country?.alphaTwoCode,
    value: String([country?.alphaTwoCode, country?.name]),
  }));

  function handleCountryFilter(countryValue: string) {
    if (checkCountryApplied(countryValue)) {
      searchParams.delete(contact_filter_key.country_code, countryValue);
    } else {
      searchParams.append(contact_filter_key.country_code, countryValue);
    }
    setSearchParams(searchParams);
  }

  useEffect(
    function initialCountrySearch() {
      if (isFilterOpen) fetchQuery({ variables: {} });
    },
    [isFilterOpen]
  );

  useEffect(
    function filteredCountrySearch() {
      function debounceCountry() {
        fetchQuery({
          variables: {
            query: countrySearch,
          },
        });
      }
      const timeoutId = setTimeout(debounceCountry, 1500);
      return () => clearTimeout(timeoutId);
    },
    [countrySearch]
  );

  return {
    countries,
    hasNextPage,
    loadNextRef,
    setCountrySearch,
    checkCountryApplied,
    handleCountryFilter,
  };
}

function useTagsFilter(isFilterOpen: boolean) {
  // search queries
  const [searchParams, setSearchParams] = useSearchParams();
  // states
  const [tagSearch, setTagSearch] = useState('');
  //apis
  const { fetchQuery, data, hasNextPage, loadNextRef } = useLazyCursorPagination<TagsV2Query, TagsV2QueryVariables>({
    schema: CURSOR_TAG_V2,
    responseDataKey: 'tagsV2',
  });

  const tags = data?.data?.map((tag) => ({
    label: tag?.label + '',
    value: String([tag?.id, tag?.label]),
  }));

  function handleTagFilter(tagValues: string[]) {
    searchParams.delete(contact_filter_key.tags);
    tagValues.map((tagValue) => searchParams.append(contact_filter_key.tags, tagValue));
    setSearchParams(searchParams);
  }

  useEffect(
    function initialTagSearch() {
      if (isFilterOpen) fetchQuery({ variables: {} });
    },
    [isFilterOpen]
  );

  useEffect(
    function filteredTagSearch() {
      function debounceTag() {
        fetchQuery({
          variables: {
            query: tagSearch,
          },
        });
      }
      const timeoutId = setTimeout(debounceTag, 1500);
      return () => clearTimeout(timeoutId);
    },
    [tagSearch]
  );

  return {
    tags,
    hasNextPage,
    loadNextRef,
    setTagSearch,
    handleTagFilter,
  };
}
